import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn, resolveLinkTarget, type ResolveLinkTargetOptions } from '../../utils';
import type { CarouselData, CarouselItem } from '../../types';
import { DisplayCarousel } from '../DisplayCarousel';
import styles from './ChatMessage.module.css';

export interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  displayCarousel?: CarouselData;
  /** Whether a carousel is being built (split text early) */
  isCarouselBuilding?: boolean;
  onCarouselButtonClick?: (item: CarouselItem, formId: string) => void;
  /** Carousel visual variant. 'program-card' renders the searchbar grid layout. */
  carouselVariant?: 'default' | 'program-card';
  /** Optional footer rendered inside the assistant bubble after the message body. */
  footer?: ReactNode;
  /** Avatar shown to the right of user messages (searchbar mode only). */
  userAvatarUrl?: string | null;
  /** Arrow shown on hover of program cards (null = default arrow icon). */
  hoverArrowUrl?: string | null;
  /** Preview mode forces links to open in a new tab. */
  previewMode?: boolean;
  /** Open same-hostname links in a new tab too (default: same window). */
  openLinksInNewTab?: boolean;
  /** Called when a link/carousel item is followed, with its resolved target. */
  onLinkNavigate?: (target: '_blank' | '_self') => void;
  className?: string;
}

const CAROUSEL_MARKER_RE = /<!--\s*CAROUSEL\s*-->/;

/**
 * Build the markdown `a` renderer shared by the intro and follow-up
 * `ReactMarkdown` instances, so the two never drift apart.
 */
function createLinkRenderer({
  previewMode,
  openLinksInNewTab,
  onLinkNavigate,
}: ResolveLinkTargetOptions & {
  onLinkNavigate?: (target: '_blank' | '_self') => void;
}): Components['a'] {
  return function MarkdownLink({ href, children }) {
    let finalHref = href;
    if (
      href &&
      !href.startsWith('/') &&
      !href.startsWith('#') &&
      !/^https?:\/\//i.test(href) &&
      !/^mailto:/i.test(href) &&
      !/^tel:/i.test(href)
    ) {
      finalHref = `https://${href}`;
    }
    const target = resolveLinkTarget(finalHref, { previewMode, openLinksInNewTab });
    return (
      <a
        className={styles.link}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        href={finalHref}
        onClick={() => onLinkNavigate?.(target)}
      >
        {children}
      </a>
    );
  };
}

/**
 * Split text into intro (before carousel) and follow-up (after carousel).
 *
 * During streaming (carouselReady=false): only split on explicit marker (stable position).
 * After carousel arrives (carouselReady=true): also use last-paragraph heuristic as fallback.
 */
function splitAroundCarousel(
  text: string,
  shouldSplit: boolean,
  carouselReady: boolean,
): { intro: string; followUp: string } {
  if (!shouldSplit) return { intro: text, followUp: '' };

  // 1. Explicit marker always wins (stable during streaming)
  const markerMatch = text.match(CAROUSEL_MARKER_RE);
  if (markerMatch) {
    return {
      intro: text.slice(0, markerMatch.index!),
      followUp: text.slice(markerMatch.index! + markerMatch[0].length),
    };
  }

  // 2. Last paragraph heuristic — only when carousel data has arrived (avoids jumpiness during streaming)
  if (carouselReady) {
    const lastBreak = text.lastIndexOf('\n\n');
    if (lastBreak > 0) {
      return {
        intro: text.slice(0, lastBreak),
        followUp: text.slice(lastBreak),
      };
    }
  }

  // 3. No split
  return { intro: text, followUp: '' };
}

export function ChatMessage({ role, content, displayCarousel, isCarouselBuilding, onCarouselButtonClick, carouselVariant = 'default', footer, userAvatarUrl, hoverArrowUrl, previewMode = false, openLinksInNewTab = false, onLinkNavigate, className }: ChatMessageProps) {
  const hasCarousel = displayCarousel && displayCarousel.items.length > 0;
  const hasExplicitMarker = CAROUSEL_MARKER_RE.test(content);
  const shouldSplit = !!hasCarousel || !!isCarouselBuilding || hasExplicitMarker;
  const { intro: introText, followUp: followUpText } = splitAroundCarousel(content, shouldSplit, !!hasCarousel);

  const hasFollowUp = !!followUpText.trim() && role === 'assistant';
  const renderFooter = !!footer && role === 'assistant';
  // Footer always anchors to the last visible bubble section so it reads as part
  // of the message: follow-up bubble if one exists, otherwise the intro bubble.
  // Carousel-with-no-followUp gets a small footer-only bubble appended.
  const footerInIntro = renderFooter && !hasCarousel && !hasFollowUp;
  const footerInFollowUp = renderFooter && hasFollowUp;
  const footerInOwnBubble = renderFooter && hasCarousel && !hasFollowUp;
  const showUserAvatar = role === 'user' && !!userAvatarUrl;
  const linkRenderer = createLinkRenderer({ previewMode, openLinksInNewTab, onLinkNavigate });

  return (
    <div
      className={cn(
        styles.message,
        role === 'user' ? styles.user : styles.assistant,
        hasCarousel && styles.withCarousel,
        className
      )}
    >
      <div className={cn(styles.content, role === 'user' && styles.userContent, showUserAvatar && styles.userContentWithAvatar)}>
        {role === 'assistant' ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <p className={styles.paragraph}>{children}</p>
              ),
              a: linkRenderer,
              strong: ({ children }) => (
                <strong className={styles.strong}>{children}</strong>
              ),
              ol: ({ children }) => (
                <ol className={styles.orderedList}>{children}</ol>
              ),
              ul: ({ children }) => (
                <ul className={styles.unorderedList}>{children}</ul>
              ),
              h1: ({ children }) => (
                <h1 className={styles.heading}>{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className={styles.heading}>{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className={styles.subheading}>{children}</h3>
              ),
              table: ({ children }) => (
                <table className={styles.table}>{children}</table>
              ),
              th: ({ children }) => (
                <th className={styles.tableHeader}>{children}</th>
              ),
              td: ({ children }) => (
                <td className={styles.tableCell}>{children}</td>
              ),
            }}
          >
            {introText}
          </ReactMarkdown>
        ) : showUserAvatar ? (
          <>
            <span className={styles.userText}>
              {content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')}
            </span>
            <img
              src={userAvatarUrl!}
              alt=""
              aria-hidden="true"
              className={styles.userAvatar}
              draggable={false}
            />
          </>
        ) : (
          content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        )}
        {footerInIntro && footer}
      </div>

      {hasCarousel && (
        <div className={styles.carouselContainer}>
          <DisplayCarousel
            items={displayCarousel.items}
            button={displayCarousel.button}
            variant={carouselVariant}
            hoverArrowUrl={hoverArrowUrl}
            onItemClick={(item) => {
              if (item.linkUrl) {
                const target = resolveLinkTarget(item.linkUrl, { previewMode, openLinksInNewTab });
                onLinkNavigate?.(target);
                if (target === '_self') {
                  window.location.href = item.linkUrl;
                } else {
                  window.open(item.linkUrl, '_blank', 'noopener,noreferrer');
                }
              }
            }}
            onButtonClick={onCarouselButtonClick}
          />
        </div>
      )}

      {footerInOwnBubble && (
        <div className={cn(styles.content, styles.followUpContent)}>
          {footer}
        </div>
      )}

      {hasFollowUp && (
        <div className={cn(styles.content, styles.followUpContent)}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <p className={styles.paragraph}>{children}</p>
              ),
              a: linkRenderer,
              strong: ({ children }) => (
                <strong className={styles.strong}>{children}</strong>
              ),
              ol: ({ children }) => (
                <ol className={styles.orderedList}>{children}</ol>
              ),
              ul: ({ children }) => (
                <ul className={styles.unorderedList}>{children}</ul>
              ),
              h1: ({ children }) => (
                <h1 className={styles.heading}>{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className={styles.heading}>{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className={styles.subheading}>{children}</h3>
              ),
              table: ({ children }) => (
                <table className={styles.table}>{children}</table>
              ),
              th: ({ children }) => (
                <th className={styles.tableHeader}>{children}</th>
              ),
              td: ({ children }) => (
                <td className={styles.tableCell}>{children}</td>
              ),
            }}
          >
            {followUpText}
          </ReactMarkdown>
          {footerInFollowUp && footer}
        </div>
      )}
    </div>
  );
}
